import React, { useEffect, useState } from 'react';
import {Outlet, useParams} from 'react-router-dom';
import styles from './personPage.module.css';
import HeaderPerson from '../headerPerson/headerPerson';
import { calculateAge } from '../../utils/calculateAge';
import { formatDate } from '../../utils/formateDate';
import { URL } from '../../utils/constants';
import Button from '../button/button';
import fetchPersonsFull from '../../api/fetchPersonsFull';
import parseCitizenshipString from "../../utils/parseCitizenshipString";
import fetchPlayerGoals from "../../api/fetchPlayerGoals";
import fetchPlayerMatches from "../../api/fetchPersonMatches";
import fetchPlayerAssists from "../../api/fetchPlayerAssists";
import fetchPersonYellowCards from "../../api/fetchPersonYellowCards";
import fetchPersonRedCards from "../../api/fetchPersonRedCards";
import fetchPlayerPenaltyGoals from "../../api/fetchPlayerPenaltyGoals";
import {Assists, Goals, Matches, PenaltyGoals, PersonFull, RedCards, YellowCards} from "../../types/personTypes";

const personMenu = [
	{
		name: 'Profile',
		link: ''
	},
	{
		name: 'Bio',
		link: 'bio'
	},
	{
		name: 'Stats',
		link: 'stats'
	},
	{
		name: 'Career',
		link: 'career'
	},
	{
		name: 'Trophies',
		link: 'trophies'
	},
	{
		name: 'News',
		link: 'news'
	},
	{
		name: 'Media',
		link: 'media'
	}
]

const PersonPage: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const [person, setPerson] = useState<PersonFull | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [goals, setGoals] = useState<Goals | null>(null);
	const [matches, setMatches] = useState<Matches | null>(null);
	const [assists, setAssists] = useState<Assists | null>(null);
	const [yellowCards, setYellowCards] = useState<YellowCards | null>(null);
	const [redCards, setRedCards] = useState<RedCards | null>(null);
	const [penaltyGoals, setPenaltyGoals] = useState<PenaltyGoals | null>(null);
	const [citizenships, setCitizenships] = useState<{ country: string, flag: string }[] | null>(null); // <-- add this state


	const getPersonsFull = async () => {
		try {
			if (id) {
				const personId = BigInt(id); // Convert the id to bigint
				const data = await fetchPersonsFull(personId);
				if (data) {
					setPerson(data);
					if (data.citizenship) {
						const parsedCitizenships = parseCitizenshipString(data.citizenship);
						setCitizenships(parsedCitizenships); // <-- set parsed citizenships in the state
					}
				}
			}
		} catch (error) {
			console.error('Error fetching person data:', error);
			setError('Failed to load person data');
		} finally {
			setLoading(false);
		}
	};

	const getPlayerGoals = async () => {
		try {
			if (id) {
				const numericId = parseInt(id, 10); // Convert string to number
				const data = await fetchPlayerGoals(numericId);
				if (data) {
					setGoals({ id: data.id, count: data.count });
				}
			}
		} catch (error) {
			console.error('Error fetching goals data:', error);
			setError('Failed to load goals data');
		} finally {
			setLoading(false);
		}
	};

	const getPlayerPenaltyGoals = async () => {
		try {
			if (id) {
				const numericId = parseInt(id, 10); // Convert string to number
				const data = await fetchPlayerPenaltyGoals(numericId);
				if (data) {
					setPenaltyGoals({ id: data.id, count: data.count });
				}
			}
		} catch (error) {
			console.error('Error fetching penalty goals data:', error);
			setError('Failed to load penalty goals data');
		} finally {
			setLoading(false);
		}
	};

	const getPlayerMatches = async () => {
		try {
			if (id) {
				const numericId = parseInt(id, 10); // Convert string to number
				const data = await fetchPlayerMatches(numericId);
				if (data) {
					setMatches({ id: data.id, count: data.count });
				}
			}
		} catch (error) {
			console.error('Error fetching matches data:', error);
			setError('Failed to load matches data');
		} finally {
			setLoading(false);
		}
	};

	const getPlayerAssists = async () => {
		try {
			if (id) {
				const numericId = parseInt(id, 10); // Convert string to number
				const data = await fetchPlayerAssists(numericId);
				if (data) {
					setAssists({ id: data.id, count: data.count });
				}
			}
		} catch (error) {
			console.error('Error fetching assists data:', error);
			setError('Failed to load assists data');
		} finally {
			setLoading(false);
		}
	};

	const getPersonYellowCards = async () => {
		try {
			if (id) {
				const numericId = parseInt(id, 10); // Convert string to number
				const data = await fetchPersonYellowCards(numericId);
				if (data) {
					setYellowCards({ id: data.id, count: data.count });
				}
			}
		} catch (error) {
			console.error('Error fetching yellow cards data:', error);
			setError('Failed to load yellow cards data');
		} finally {
			setLoading(false);
		}
	};

	const getPersonRedCards = async () => {
		try {
			if (id) {
				const numericId = parseInt(id, 10); // Convert string to number
				const data = await fetchPersonRedCards(numericId);
				if (data) {
					setRedCards({ id: data.id, count: data.count });
				}
			}
		} catch (error) {
			console.error('Error fetching red cards data:', error);
			setError('Failed to load red cards data');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getPersonsFull();
		getPlayerGoals();
		getPlayerPenaltyGoals();
		getPlayerMatches();
		getPlayerAssists();
		getPersonYellowCards();
		getPersonRedCards();
	}, [id]);

	if (loading) {
		return <div>Loading...</div>;
	}

	if (error) {
		return <div>Error: {error}</div>;
	}

	if (!person) {
		return <div>No person found</div>;
	}

	return (
		<>
		<HeaderPerson person={person} menu={personMenu}/>
		<main>
			<Outlet context={person}/>
			<div>
				<section className={styles.player_profile}>
					<div className={styles.player_details}>
						<ul className={styles.details_list}>
							<li>
								Дата рождения<span>{formatDate(person.birth_date)}</span>
							</li>
							<li>
								Возраст<span>{calculateAge(person.birth_date)}</span>
							</li>
							{person.place_of_birth && (
								<li>
									Место рождения<span>{person.place_of_birth}</span>
								</li>
							)}
							{person.citizenship && (

								<li>
									Гражданство
									<span>
{citizenships &&
	citizenships.map((citizenship, index) => (
	<span key={index}>
          <img
			  src={`${URL}/${citizenship.flag}`}
			  alt={citizenship.country}
			  style={{ marginRight: '5px', width: '20px', height: 'auto' }}
		  />
		{citizenship.country}
		{index < citizenships.length - 1 && ', '}
        </span>
))}
    								</span>
								</li>
							)}
							{person.languages && (
								<li>
									Языки<span>{person.languages}</span>
								</li>
							)}
							{person.height && (
								<li>
									Рост<span>{person.height} см</span>
								</li>
							)}
							{person.weight && (
								<li>
									Вес<span>{person.weight} кг</span>
								</li>
							)}
							{person.coaching_license && (
								<li>
									Лицензия<span>{person.coaching_license}</span>
								</li>
							)}
							{person.referee_category && (
								<li>
									Категория<span>{person.referee_category}</span>
								</li>
							)}
						</ul>
					</div>
					<div className={styles.player_national_team}>
						<p>
							Информация об играх за сборную пока недоступна, но скоро сделаем!
						</p>
					</div>
				</section>

				<section>
					<h2 className={styles.section_header}>Статистика за карьеру</h2>
					<ul className={styles.player_stats}>
						<li>
							Матчей
							<span>{matches?.count}</span>
						</li>
						<li>
							Забитых голов
							<span>{goals?.count}</span>
						</li>
						<li>
							Голов с пенальти
							<span>{penaltyGoals?.count}</span>
						</li>
						<li>
							Голевых передач
							<span>{assists?.count}</span>
						</li>
						<li>
							Жёлтых карточек
							<span>{yellowCards?.count}</span>
						</li>
						<li>
							Красных карточек
							<span>{redCards?.count}</span>
						</li>
						<li>
							Командных наград
							<span>0</span>
						</li>
					</ul>
					<div className={styles.button_container}>
						<Button text={'Полная статистика'} arrow={true} />
					</div>
				</section>
			</div>
		</main>
		</>

	);
};

export default PersonPage;
